import React, { useState, useEffect, FC } from 'react';
import {
  Box,
  Button,
  Grid,
  Paper,
  Typography,
  ThemeProvider,
  createTheme,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Divider,
  Tooltip,
  IconButton
} from '@mui/material';
import { styled } from '@mui/material/styles';
import {
  PeopleAlt,
  Add,
  Remove,
  Info,
  Refresh,
} from '@mui/icons-material';

// --- Props Interface ---
interface ArtistFeeCalculatorProps {
  initialValue?: number;
  initialMode?: 'artistFee' | 'calculator'; // ⭐ NEW: Allow setting initial mode
  onSubmit?: (value: number) => void;
  onClose?: () => void;
}

// --- Theme setup ---
const theme = createTheme({
  palette: {
    primary: { main: '#102E44' },
    secondary: { main: '#3A9188' },
    error: { main: '#E57373' },
    success: { main: '#81C784' },
    info: { main: '#64B5F6' },
    warning: { main: '#FFB74D' },
    background: { default: '#21324a', paper: '#34495E' },
    text: { primary: '#FFFFFF', secondary: '#B0BEC5' },
  },
  typography: { fontFamily: "'Roboto', 'Helvetica', 'Arial', sans-serif" },
  components: {
    MuiButton: {
      styleOverrides: {
        root: { borderRadius: 8 },
        outlined: {
          borderColor: 'rgba(255,255,255,0.7)',
          color: '#FFFFFF',
        },
        contained: {
          color: '#FFFFFF',
        },
      },
    },
    MuiToggleButton: {
      styleOverrides: {
        root: {
          color: '#FFFFFF',
          borderColor: 'rgba(255,255,255,0.7)',
          '&.Mui-selected': {
            backgroundColor: '#3A9188',
            color: '#FFFFFF',
          },
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: { borderRadius: 12 },
      },
    },
  },
});

// --- Styled components ---
const CalcButton = styled(Button)(({ theme }) => ({
  width: '100%',
  height: '56px',
  fontSize: '1.3rem',
  fontWeight: 'bold',
  boxShadow: '0 4px 10px rgba(0,0,0,0.15)',
}));
const OperationButton = styled(CalcButton)(({ theme }) => ({ backgroundColor: theme.palette.secondary.main }));
const NumberButton = styled(CalcButton)(({ theme }) => ({ backgroundColor: theme.palette.warning.main }));
const ClearButton = styled(CalcButton)(({ theme }) => ({ backgroundColor: theme.palette.error.main }));
const EqualsButton = styled(CalcButton)(({ theme }) => ({ backgroundColor: theme.palette.info.main }));
const Display = styled(Paper)(({ theme }) => ({
  width: '100%',
  padding: theme.spacing(2),
  marginBottom: theme.spacing(2),
  backgroundColor: '#1E2A38',
  color: '#FFFFFF',
  boxShadow: '0 4px 15px rgba(0,0,0,0.2)',
  borderRadius: 12,
}));

// --- Component ---
const ArtistFeeCalculator: FC<ArtistFeeCalculatorProps> = ({
  initialValue = 0,
  initialMode = 'artistFee', // ⭐ NEW: Default to artistFee but allow override
  onSubmit = () => {},
  onClose = () => {},
}) => {
  // ⭐ UPDATED: Use initialMode prop instead of hardcoded default
  const [mode, setMode] = useState<'artistFee' | 'calculator'>(initialMode);

  // common calculator state
  const [display, setDisplay] = useState<string>(initialValue.toString());
  const [waitingForOperand, setWaitingForOperand] = useState<boolean>(true);
  const [pendingOperator, setPendingOperator] = useState<string | null>(null);
  const [storedValue, setStoredValue] = useState<number | null>(null);

  // artist fee state
  const [bandSize, setBandSize] = useState<number>(1);
  const [baseFee, setBaseFee] = useState<number>(initialValue);
  const [finalFee, setFinalFee] = useState<number>(initialValue);
  const [tipPercentage, setTipPercentage] = useState<number>(0);
  const [adjustmentType, setAdjustmentType] = useState<'fixed' | 'percentage'>('fixed');
  const [adjustment, setAdjustment] = useState<number>(0);

  const groupSizes: number[] = [1, 2, 3, 4, 5, 6, 7, 8, 10, 12, 15];

  // ⭐ UPDATED: Also sync initialMode when props change
  useEffect(() => {
    setDisplay(initialValue.toString());
    setBaseFee(initialValue);
    setMode(initialMode); // Sync mode with prop
    recalc(initialValue, bandSize, tipPercentage, adjustment, adjustmentType);
  }, [initialValue, initialMode]);

  // whenever display changes (from calculator), update baseFee and recalc
  useEffect(() => {
    if (mode === 'artistFee') return;
    const val = parseFloat(display) || 0;
    setBaseFee(val);
    recalc(val, bandSize, tipPercentage, adjustment, adjustmentType);
  }, [display, mode, bandSize, tipPercentage, adjustment, adjustmentType]);

  // recalc final fee
  const recalc = (
    base: number,
    size: number,
    tip: number,
    adj: number,
    adjType: 'fixed' | 'percentage'
  ): void => {
    // calculate total base across all members
    const totalBase = base * size;

    // calculate adjustment amount on the total base
    const adjustmentAmount =
      adjType === 'fixed'
        ? adj
        : (totalBase * adj) / 100;

    // apply adjustment
    const adjustedTotal = totalBase + adjustmentAmount;

    // apply tip/bonus
    const withTip = adjustedTotal * (1 + tip / 100);

    // update total fee
    setFinalFee(withTip);
  };

  // Artist Fee handlers
  const changeBandSize = (size: number): void => {
    setBandSize(size);
    recalc(baseFee, size, tipPercentage, adjustment, adjustmentType);
  };
  const changeTip = (pct: number): void => {
    setTipPercentage(pct);
    recalc(baseFee, bandSize, pct, adjustment, adjustmentType);
  };
  const changeAdj = (val: number): void => {
    setAdjustment(val);
    recalc(baseFee, bandSize, tipPercentage, val, adjustmentType);
  };
  const changeAdjType = (type: 'fixed' | 'percentage'): void => {
    setAdjustmentType(type);
    recalc(baseFee, bandSize, tipPercentage, adjustment, type);
  };
  const resetAll = (): void => {
    setDisplay(initialValue.toString());
    setBaseFee(initialValue);
    setBandSize(1);
    setTipPercentage(0);
    setAdjustment(0);
    setAdjustmentType('fixed');
    recalc(initialValue, 1, 0, 0, 'fixed');
    setPendingOperator(null);
    setStoredValue(null);
    setWaitingForOperand(true);
  };

  // Calculator handlers
  const inputDigit = (digit: string): void => {
    if (waitingForOperand) {
      setDisplay(digit);
      setWaitingForOperand(false);
    } else {
      setDisplay(display === '0' ? digit : display + digit);
    }
  };
  const inputDot = (): void => {
    if (waitingForOperand) {
      setDisplay('0.');
      setWaitingForOperand(false);
    } else if (!display.includes('.')) {
      setDisplay(display + '.');
    }
  };
  const clearCalc = (): void => {
    setDisplay('0');
    setWaitingForOperand(true);
    setPendingOperator(null);
    setStoredValue(null);
  };
  const doOperation = (op: string): void => {
    const current = parseFloat(display);
    if (pendingOperator && storedValue !== null) {
      const result = calculate(storedValue, current, pendingOperator);
      setStoredValue(result);
      setDisplay(result.toString());
    } else {
      setStoredValue(current);
    }
    setPendingOperator(op);
    setWaitingForOperand(true);
  };
  const calculate = (x: number, y: number, op: string): number => {
    switch (op) {
      case '+': return x + y;
      case '-': return x - y;
      case '*': return x * y;
      case '/': return x / y;
      default: return y;
    }
  };
  const handleEquals = (): void => {
    if (pendingOperator && storedValue !== null) {
      const current = parseFloat(display);
      const result = calculate(storedValue, current, pendingOperator);
      setDisplay(result.toString());
      setStoredValue(null);
      setPendingOperator(null);
      setWaitingForOperand(true);
      // apply result to fee
      setBaseFee(result);
      recalc(result, bandSize, tipPercentage, adjustment, adjustmentType);
      // switch back to artist fee mode
      setMode('artistFee');
    }
  };

  return (
    <ThemeProvider theme={theme}>
      <Box sx={{ maxWidth: 500, mx: 'auto', p: 3, bgcolor: 'background.default', borderRadius: 4 }}>
        <Typography variant="h4" align="center" gutterBottom sx={{ color: 'text.primary' }}>
          {mode === 'artistFee' ? 'Artist Fee Calculator' : 'Regular Calculator'}
        </Typography>

        {/* Mode Toggle */}
        <ToggleButtonGroup
          value={mode}
          exclusive
          onChange={(_e, val) => val && setMode(val)}
          fullWidth
          sx={{ mb: 2 }}
        >
          <ToggleButton value="artistFee">Artist Fee</ToggleButton>
          <ToggleButton value="calculator">Calculator</ToggleButton>
        </ToggleButtonGroup>

        {mode === 'calculator' ? (
         <>
  <Display><Typography align="right" variant="h3">{display}</Typography></Display>

  <Grid container spacing={1}>
    <Grid item xs={3}><ClearButton onClick={clearCalc}>C</ClearButton></Grid>
    <Grid item xs={3}><OperationButton onClick={() => doOperation('/')}>/</OperationButton></Grid>
    <Grid item xs={3}><OperationButton onClick={() => doOperation('*')}>×</OperationButton></Grid>
    <Grid item xs={3}><OperationButton onClick={() => doOperation('-')}>-</OperationButton></Grid>
    {[7,8,9].map(n => (<Grid item xs={3} key={n}><NumberButton onClick={() => inputDigit(n.toString())}>{n}</NumberButton></Grid>))}
    <Grid item xs={3}><OperationButton onClick={() => doOperation('+')}>+</OperationButton></Grid>
    {[4,5,6].map(n => (<Grid item xs={3} key={n}><NumberButton onClick={() => inputDigit(n.toString())}>{n}</NumberButton></Grid>))}
    <Grid item xs={3}><OperationButton onClick={() => doOperation('+')}>+</OperationButton></Grid>
    {[1,2,3].map(n => (<Grid item xs={3} key={n}><NumberButton onClick={() => inputDigit(n.toString())}>{n}</NumberButton></Grid>))}
    <Grid item xs={3}><OperationButton onClick={() => doOperation('+')}>+</OperationButton></Grid>
    <Grid item xs={3}><NumberButton onClick={() => inputDigit('0')}>0</NumberButton></Grid>
    <Grid item xs={3}><NumberButton onClick={inputDot}>.</NumberButton></Grid>
    <Grid item xs={3}><EqualsButton onClick={handleEquals}>=</EqualsButton></Grid>
  </Grid>

  <Grid container spacing={2} sx={{ mt: 2 }}>
    <Grid item xs={6}>
      <Button
        fullWidth
        variant="outlined"
        startIcon={<Refresh />}
        onClick={clearCalc}
      >
        Reset
      </Button>
    </Grid>
    <Grid item xs={6}>
      <Button
        fullWidth
        variant="contained"
        color="success"
        onClick={() => onSubmit(parseFloat(display) || 0)}
      >
        Submit Result
      </Button>
    </Grid>
  </Grid>
</>
        ) : (
          <>          
            <Paper sx={{ p: 2, mb: 2, bgcolor: '#1E2A38' }}>
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <Typography variant="subtitle2" color="text.secondary">Base Fee</Typography>
                  <TextField
                    fullWidth
                    size="small"
                    type="number"
                    value={baseFee}
                    onChange={e => {
                      const val = parseFloat(e.target.value) || 0;
                      setBaseFee(val);
                      recalc(val, bandSize, tipPercentage, adjustment, adjustmentType);
                    }}
                    InputProps={{ startAdornment: <Typography>€</Typography> }}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        backgroundColor: 'rgba(255,255,255,0.05)',
                        color: theme.palette.text.primary
                      }
                    }}
                  />
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="subtitle2" color="text.secondary">Group Size</Typography>
                  <Box display="flex" alignItems="center">
                    <IconButton size="small" onClick={() => changeBandSize(Math.max(1, bandSize - 1))} disabled={bandSize <= 1}><Remove/></IconButton>
                    <Typography variant="h6" sx={{ mx: 1, color: 'info.main' }}>{bandSize}</Typography>
                    <IconButton size="small" onClick={() => changeBandSize(bandSize + 1)}><Add/></IconButton>
                    <PeopleAlt sx={{ ml: 1, color: 'info.main' }}/>
                  </Box>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="subtitle2" color="text.secondary">Fee Per Person</Typography>
                  <Typography variant="h6" sx={{ color: 'info.main' }}>€{(finalFee / bandSize).toFixed(2)}</Typography>
                </Grid>
                <Grid item xs={12}><Divider sx={{ bgcolor: 'rgba(255,255,255,0.1)' }}/></Grid>
                <Grid item xs={12}>
                  <Typography variant="subtitle2" color="text.secondary">Tip/Bonus ({tipPercentage}%)</Typography>
                  <Box display="flex" mb={1}>
                    {[0,5,10,15,20].map(p => (
                      <Button
                        key={p}
                        variant="outlined"
                        size="small"
                        onClick={() => changeTip(p)}
                      >
                        {p}%
                      </Button>
                    ))}
                  </Box>
                </Grid>
                <Grid item xs={12}>
                  <Box display="flex" alignItems="center" mb={1}>
                    <Typography variant="subtitle2" color="text.secondary" sx={{ mr: 1 }}>Adjustment</Typography>
                    <Button size="small" variant={adjustmentType === 'fixed' ? 'contained' : 'outlined'} onClick={() => changeAdjType('fixed')} sx={{ mr: 1 }}>€</Button>
                    <Button size="small" variant={adjustmentType === 'percentage' ? 'contained' : 'outlined'} onClick={() => changeAdjType('percentage')}>%</Button>
                    <Tooltip title="Add/subtract a fixed amount or percentage"><IconButton size="small"><Info/></IconButton></Tooltip>
                  </Box>
                  <TextField
                    fullWidth
                    size="small"
                    type="number"
                    InputProps={{ startAdornment: adjustmentType === 'fixed' ? <Typography>€</Typography> : <Typography>%</Typography> }}
                    value={adjustment}
                    onChange={e => changeAdj(parseFloat(e.target.value) || 0)}
                  />
                </Grid>
                <Grid item xs={12}><Divider sx={{ mb: 2, bgcolor: 'rgba(255,255,255,0.1)' }}/></Grid>
                <Grid item xs={12}>
                  <Typography variant="subtitle2" color="text.secondary">TOTAL FEE</Typography>
                  <Typography variant="h3" sx={{ color: 'success.main', fontWeight: 'bold' }}>€{finalFee.toFixed(2)}</Typography>
                </Grid>
              </Grid>
            </Paper>
            <Grid container spacing={2}>
              <Grid item xs={6}><Button fullWidth variant="outlined" startIcon={<Refresh />} onClick={resetAll}>Reset</Button></Grid>
              <Grid item xs={6}><Button fullWidth variant="contained" color="success" onClick={() => onSubmit(finalFee)}>Submit Fee</Button></Grid>
            </Grid>
          </>
        )}

        <Box mt={2} textAlign="center">
          <Button variant="text" onClick={onClose}>Cancel</Button>
        </Box>
      </Box>
    </ThemeProvider>
  );
};

export default ArtistFeeCalculator;